// internal-deps
import { SwgyHeadersKeys } from "@swiggy-private/checkout-types";
import { LocalStorage, Storage } from "@swiggy-private/core-ui";

import { isEmpty } from "./Core";

export type SwgyHeaderType = { [key in Partial<SwgyHeadersKeys>]: string };
let swiggyHeaders: SwgyHeaderType = {} as SwgyHeaderType;

// TODO: remove this..
const setHeader = (): void => {
    if (!isEmpty(window.__headers__)) {
        swiggyHeaders = window.__headers__ as SwgyHeaderType;
    }
};

export class GlobalHeaderState {
    static init(initialHeaders: SwgyHeaderType): void {
        if (initialHeaders) {
            swiggyHeaders = initialHeaders;

            // sync headers in local storage and session storage to make sure
            // updated headers will not be lost during redirections
            // DO NOT stringify these values, it is already done by our wrapper on both storages.
            // IT WILL BREAK THINGS IF WE STRINGIFY
            Storage.setItem("headers", initialHeaders);
            LocalStorage.setItem("auth_headers", initialHeaders);
        }
    }

    static getHeaders = (): SwgyHeaderType => {
        if (!swiggyHeaders) {
            setHeader();
        }

        return swiggyHeaders;
    };

    static getHeader = (key: string): string => {
        if (!swiggyHeaders) {
            setHeader();
        }

        if (typeof key !== "string" || !swiggyHeaders) {
            throw new Error(`Error while getting header with ${key}`);
        }

        return swiggyHeaders[key.toLowerCase() as SwgyHeadersKeys];
    };

    /*
     * This function is added to support updating headers realtime as a requirement mainly in swiggy money mandates
     * where the businessline updates dynamically depending on user's actions. e.g., clicking on add swiggy
     * money cta, will update businessline to swiggy money, and coming back to payments or after topup successful,
     * the businessline will be reset again to original business line.
     */
    static updateHeaders = (params: { [key: string]: string | null }): void => {
        const currentHeaders = GlobalHeaderState.getHeaders();
        swiggyHeaders = {
            ...currentHeaders,
            ...(params as Record<string, string>),
        };
        window.__headers__ = swiggyHeaders as SwgyHeaderType;

        // sync headers in local storage and session storage to make sure
        // updated headers will not be lost during redirections
        // DO NOT stringify these values, it is already done by our wrapper on both storages.
        // IT WILL BREAK THINGS IF WE STRINGIFY
        Storage.setItem("headers", swiggyHeaders);
        LocalStorage.setItem("auth_headers", swiggyHeaders);
    };

    static setHeader = (key: SwgyHeadersKeys, value: string): void => {
        if (typeof key !== "string" || typeof value !== "string" || !swiggyHeaders) {
            throw new Error(`Error while setting header with ${key} ${value}`);
        }

        swiggyHeaders[key] = value;
    };

    static removeHeader = (key: SwgyHeadersKeys): void => {
        delete swiggyHeaders[key];
    };

    /**
     * Removes a header from all storages (in-memory, session, and local storage).
     * @param key - The header key to remove (string, should be lower case and match storage keys)
     */
    static removeHeaderFromAllStorages(key: string): void {
        try {
            // Remove from in-memory headers
            if (swiggyHeaders && swiggyHeaders[key as SwgyHeadersKeys]) {
                delete swiggyHeaders[key as SwgyHeadersKeys];
            }

            // Remove from session storage
            const sessionHeaders = Storage.getItem<{ [key: string]: string }>("headers");
            if (sessionHeaders != null && sessionHeaders[key] != null) {
                delete sessionHeaders[key];
                Storage.setItem<{ [key: string]: string }>("headers", sessionHeaders);
            }

            // Remove from local storage
            const localHeaders = LocalStorage.getItem<{ [key: string]: string }>("auth_headers");
            if (localHeaders != null && localHeaders[key] != null) {
                delete localHeaders[key];
                LocalStorage.setItem<{ [key: string]: string }>("auth_headers", localHeaders);
            }
        } catch {
            if (__DEV__) {
                // eslint-disable-next-line no-console
                console.log(`unable to remove header: ${key}`);
            }
        }
    }
}
